import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useClerk } from '@clerk/clerk-react';
import constants from '@playcastdotio/GlobalConfig';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import { RewardsCoordinator } from '../../coordinators/rewards/RewardsCoordinator';
import { RewardsCoordinatorContext } from '../../coordinators/rewards/RewardsCoordinatorContext';
import { RewardsService } from '../../services/rewards/RewardsService';
import { serviceContainer } from '../../services/ServiceContainer';
import { machineIdAttestationState } from '../../api/stores/machineIdAttestationState';
import { machineIdState } from '../../api/stores/shared';
import { getElectronStores } from '../../api/stores/electronStores';
import { useStoreListener } from '../../useStoreListener';
import {
  PAVILION_SHELL_BORDER,
  PAVILION_SHELL_GRADIENT,
  PAVILION_SHELL_RADIUS,
} from './pavilionShellStyles';
import { stripColorTags } from './rewards/utils/parseColoredText';
import {
  RewardsHub,
  CatalogueList,
  RedeemPage,
  RedemptionHistory,
  AccountPage,
  SettingsPage,
  HowPointsWork,
  PointsActivity,
  ProgramTerms,
} from './rewards';
import { WelcomeToPlaycast } from './rewards/WelcomeToPlaycast';
import { HowToReachTier } from './rewards/HowToReachTier';
import { TierDetailScreen } from './rewards/TierDetailScreen';
import { AboutTiers } from './rewards/AboutTiers';
import { AddThisPC } from './rewards/AddThisPC';
import { useTierEvaluation } from './rewards/hooks/useTierEvaluation';
import { useOnboardingWelcome } from './rewards/hooks/useOnboardingWelcome';
import { OnboardingFlowManager } from '../../coordinators/rewards/managers/OnboardingFlowManager';
import { loginProfileState } from '../../api/stores/shared';
import { ShellToastProvider } from './rewards/Toast';

type RewardsScreen =
  | 'hub'
  | 'catalogue'
  | 'redeem'
  | 'history'
  | 'settings'
  | 'account'
  | 'how-points'
  | 'points-activity'
  | 'about-tiers'
  | 'settings-tier-detail'
  | 'program-terms'
  | 'onboarding-welcome'
  | 'onboarding-learn'
  | 'onboarding-add-pc';

interface NavState {
  screen: RewardsScreen;
  selectedSkuId?: string;
  previousScreen?: RewardsScreen;
}

export const PavilionPostLogin = () => {
  const context = useContext(PlaycastContext);
  const { signOut } = useClerk();
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [schedulePageVisible, setSchedulePageVisible] = useState(false);

  // ── Onboarding Flow Manager ──────────────────────────────────────────
  // Two onboarding gates evaluated in order: Welcome → Add PC.
  // Reboot state is handled by the hub variant system (not an onboarding screen).

  // Gate 1: Attestation status (drives both "pending" loading and the Add PC gate)
  const isLocalBranch = constants.BRANCH === 'local';
  const attestState = useStoreListener<{ status: string }>(machineIdAttestationState);
  const attestStatus = attestState.status;
  const isPending = !isLocalBranch && (attestStatus === 'checking' || attestStatus === 'attesting');
  const needsAttestation = isLocalBranch || attestStatus === 'idle' || attestStatus === 'failed';

  // Driver health — passed to the hub to determine normal vs pending-restart variant.
  // filterDriverMouse/Keyboard are kernel upper-filter drivers that only load after a reboot.
  // ShadowCam03 is excluded: it's a virtual device activated by PlayJector ~700ms after boot.
  const rawDriverState = useStoreListener<Record<string, boolean>>(getElectronStores().driverState);
  const driversPopulated = isLocalBranch || Object.keys(rawDriverState).length > 0;
  const driversHealthy = !isLocalBranch && driversPopulated && !!rawDriverState.filterDriverMouse && !!rawDriverState.filterDriverKeyboard;

  // Gate 3: Welcome screen (userId mismatch — localStorage cleared on reinstall)
  const loginProfile = useStoreListener<{ hostAuthId?: string }>(loginProfileState);
  const userId = loginProfile?.hostAuthId || null;
  const { needsWelcome, evaluated: welcomeEvaluated, markSeen: markWelcomeSeen } = useOnboardingWelcome(userId);

  // Manager instance (stable for component lifetime)
  const [onboardingManager] = useState(() => new OnboardingFlowManager());

  const [nav, setNav] = useState<NavState | null>(null);
  const [gateTimedOut, setGateTimedOut] = useState(false);

  // Subscribe reactively to machineIdState so tier polling starts when the ID arrives
  const reactiveMachineId = useStoreListener<string>(machineIdState);
  const currentMachineId = reactiveMachineId || null;

  // Tier data is pre-computed on the Machine doc — just fetch once when machineId is available
  const { tier: evaluatedTier, nextTier: evaluatedNextTier, previousTier: evaluatedPreviousTier, loading: tierLoading, retry: retryTierEvaluation, initialFetchDone, initialFetchFailed } = useTierEvaluation(currentMachineId);

  // 45s safety timeout — if gates never settle, show an error state rather than
  // spinning forever (e.g. PlayJector never sends driverState).
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!nav) setGateTimedOut(true);
    }, 45_000);
    return () => clearTimeout(timer);
  }, [nav]);

  // Evaluate gates once all inputs have settled
  useEffect(() => {
    if (isPending) {
      console.debug('[OnboardingFlow] Waiting — attestation still pending:', attestStatus);
      return;
    }
    if (!driversPopulated) {
      console.debug('[OnboardingFlow] Waiting — driverState not yet populated from PlayJector');
      return;
    }
    if (!welcomeEvaluated) {
      console.debug('[OnboardingFlow] Waiting — welcome gate not yet evaluated');
      return;
    }
    if (!userId) {
      console.debug('[OnboardingFlow] Waiting — userId not available yet');
      return;
    }
    if (onboardingManager.isEvaluated()) return;

    const effectiveNeedsWelcome = isLocalBranch || needsWelcome;

    // When needsWelcome is false, wait for the initial tier fetch (a quick REST
    // call) to determine whether the machine has no tier. Don't wait for the
    // full 150s tier loading — only the initial GET.
    if (!effectiveNeedsWelcome && !initialFetchDone) {
      console.debug('[OnboardingFlow] Waiting — initial tier fetch not yet done');
      return;
    }

    const needsWelcomeNoTier = !effectiveNeedsWelcome && initialFetchDone && !initialFetchFailed && evaluatedTier === null;

    onboardingManager.evaluate({
      needsWelcome: effectiveNeedsWelcome,
      needsWelcomeNoTier,
      needsAttestation,
    });

    const screen = onboardingManager.getCurrentScreen();
    setNav({ screen: screen as RewardsScreen });
  }, [isPending, attestStatus, driversPopulated, userId, welcomeEvaluated, needsWelcome, needsAttestation, onboardingManager, initialFetchDone, initialFetchFailed, evaluatedTier]);

  const coordinator = useMemo(() => {
    const service = new RewardsService();
    return new RewardsCoordinator({
      rewardsService: service,
      nonsoleReadinessService: serviceContainer.nonsoleReadinessService,
    });
  }, []);

  useEffect(() => {
    coordinator.init();
    serviceContainer.analyticsManager.sendRewardsHubOpenedEvent();
    return () => coordinator.destroy();
  }, [coordinator]);

  // Keep host messages filtered to the machine's assigned tier
  useEffect(() => {
    coordinator.setMachineTierId(evaluatedTier?.tierId ?? null);
  }, [coordinator, evaluatedTier?.tierId]);

  const handleClose = () => {
    context.electronFunctions?.setWindowState('closed');
  };

  const navigateTo = useCallback((screen: RewardsScreen, skuId?: string) => {
    if (screen === 'hub') {
      coordinator.refreshMessages();
    }
    if (screen === 'catalogue') {
      coordinator.refreshCatalogue();
      coordinator.refreshRestock();
      serviceContainer.analyticsManager.sendCatalogueViewedEvent();
    }
    if (screen === 'redeem') {
      coordinator.refreshCatalogue();
    }
    setNav((prev) => ({ screen, selectedSkuId: skuId, previousScreen: prev?.screen }));
  }, [coordinator]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    window.location.reload();
  }, [signOut]);

  const handleSignOutRequest = useCallback(() => {
    setShowSignOutConfirm(true);
  }, []);

  const renderScreen = () => {
    if (!nav) return null;
    switch (nav.screen) {
      case 'catalogue':
        return (
          <CatalogueList
            onSelectItem={(skuId) => navigateTo('redeem', skuId)}
            onBack={() => navigateTo('hub')}
          />
        );

      case 'redeem':
        return nav.selectedSkuId ? (
          <RedeemPage
            skuId={nav.selectedSkuId}
            onBack={() => navigateTo('catalogue')}
            onViewHistory={() => navigateTo('history')}
            onBackToRewards={() => navigateTo('hub')}
          />
        ) : null;

      case 'history':
        return <RedemptionHistory onBack={() => navigateTo('settings')} />;

      case 'settings':
        return (
          <SettingsPage
            onBack={() => navigateTo('hub')}
            onNavigate={(s) => navigateTo(s)}
            onSignOut={handleSignOutRequest}
          />
        );

      case 'account':
        return (
          <AccountPage
            onBack={() => navigateTo('settings')}
            onNavigate={(s) => navigateTo(s)}
          />
        );

      case 'how-points':
        return (
          <HowPointsWork
            onBack={() => navigateTo('settings')}
            onNavigate={(s) => navigateTo(s)}
          />
        );

      case 'points-activity':
        return <PointsActivity
          onBack={() => navigateTo(nav.previousScreen === 'how-points' ? 'how-points' : 'account')}
          backLabel={nav.previousScreen === 'how-points' ? 'How Tokens Work' : 'Account'}
        />;

      case 'about-tiers':
        return (
          <AboutTiers
            tier={evaluatedTier}
            nextTier={evaluatedNextTier}
            previousTier={evaluatedPreviousTier}
            loading={tierLoading}
            onBack={() => navigateTo('settings')}
            onLearnMore={() => navigateTo('settings-tier-detail')}
          />
        );

      case 'settings-tier-detail':
        return (
          <TierDetailScreen
            tierLoading={tierLoading}
            evaluatedTier={evaluatedTier}
            evaluatedNextTier={evaluatedNextTier}
            onBack={() => navigateTo('about-tiers')}
          />
        );

      case 'program-terms':
        return <ProgramTerms onBack={() => navigateTo('settings')} />;

      case 'hub':
      default:
        return (
          <>
            <RewardsHub
              onNavigate={(s) => navigateTo(s)}
              onNavigateRedeem={(skuId) => navigateTo('redeem', skuId)}
              driversHealthy={driversHealthy}
              onScheduleVisibleChange={setSchedulePageVisible}
            />
          </>
        );

      case 'onboarding-welcome':
        return (
          <WelcomeToPlaycast
            tier={evaluatedTier}
            nextTier={evaluatedNextTier}
            previousTier={evaluatedPreviousTier}
            loading={tierLoading}
            onRetry={retryTierEvaluation}
            onContinue={() => {
              markWelcomeSeen();
              onboardingManager.advance();
              const next = onboardingManager.getCurrentScreen();
              navigateTo(next as RewardsScreen);
            }}
            onLearnMore={() => navigateTo('onboarding-learn')}
          />
        );

      case 'onboarding-learn': {
        const currentName = evaluatedTier?.name ?? 'Tier 1';
        const currentSummary = evaluatedTier?.detailSummary ?? '';
        const targetName = evaluatedNextTier?.name ?? 'Tier 2';
        const targetRate = stripColorTags(evaluatedNextTier?.earnRateDescription);
        const targetMax = stripColorTags(evaluatedNextTier?.maxEarningsDescription);
        const targetSummary = [
          'Clear every check below',
          targetRate ? ` — ${targetRate.toLowerCase()}` : '',
          targetMax ? ` ${targetMax}` : '',
        ].join('');
        return (
          <HowToReachTier
            currentTierName={currentName}
            currentTierId={evaluatedTier?.tierId}
            currentEarnSummary={currentSummary}
            targetTierName={targetName}
            targetTierId={evaluatedNextTier?.tierId}
            targetEarnSummary={targetSummary}
            componentChecks={evaluatedNextTier?.componentChecks ?? []}
            onBack={() => navigateTo('onboarding-welcome')}
          />
        );
      }

      case 'onboarding-add-pc':
        return (
          <AddThisPC
            onAttestation={async () => {
              // On local dev, skip real attestation (no PlayJector/TPM) and advance
              if (isLocalBranch) {
                console.debug('[OnboardingFlow] Local branch — skipping attestation, advancing');
                onboardingManager.advance();
                const next = onboardingManager.getCurrentScreen();
                navigateTo(next as RewardsScreen);
                return;
              }

              const { handleClerkSessionUpdate } = await import('../../api/services/machineIdAuthIntegration');
              const { loginProfileState } = await import('../../api/stores/shared');
              const authId = loginProfileState.currentState?.hostAuthId;
              if (!authId) {
                throw new Error('No authenticated user found');
              }

              handleClerkSessionUpdate(authId);

              const ATTEST_TIMEOUT_MS = 30_000;
              const terminalStatus = await new Promise<string>((resolve, reject) => {
                const currentStatus = machineIdAttestationState.currentState.status;
                if (currentStatus === 'attested' || currentStatus === 'not_needed' || currentStatus === 'failed') {
                  resolve(currentStatus);
                  return;
                }

                const timeout = setTimeout(() => {
                  machineIdAttestationState.removeListener(listenerId);
                  reject(new Error('Timeout waiting for attestation to complete'));
                }, ATTEST_TIMEOUT_MS);

                const listenerId = machineIdAttestationState.addListener((state) => {
                  if (state.status === 'attested' || state.status === 'not_needed' || state.status === 'failed') {
                    clearTimeout(timeout);
                    machineIdAttestationState.removeListener(listenerId);
                    resolve(state.status);
                  }
                });
              });

              if (terminalStatus === 'attested' || terminalStatus === 'not_needed') {
                onboardingManager.advance();
                const next = onboardingManager.getCurrentScreen();
                navigateTo(next as RewardsScreen);
              } else {
                const errorMsg = machineIdAttestationState.currentState.error
                  ?? `Attestation failed with status: ${terminalStatus}`;
                throw new Error(errorMsg);
              }
            }}
            onSkip={handleSignOut}
          />
        );

    }
  };

  // While attestation is still pending, mirror the login screen with a
  // non-clickable button showing a loading spinner. This prevents the
  // onboarding Welcome screen from flashing before settling to the hub.
  if (isPending || !nav) {
    const handleRetryGates = () => {
      setGateTimedOut(false);
      window.location.reload();
    };

    return (
      <div className='box-border flex h-full min-h-0 w-full flex-1 overflow-hidden' style={{ background: '#3D2A69' }}>
        <div
          className={`relative box-border flex h-full min-h-0 w-full flex-col overflow-hidden ${PAVILION_SHELL_RADIUS} ${PAVILION_SHELL_BORDER} ${PAVILION_SHELL_GRADIENT} pt-5 pb-5 pl-[7px] pr-[7px]`}
        >
          <button type='button' onClick={handleClose}
            className='absolute right-[14px] top-[14px] z-10 flex items-center justify-center transition-opacity hover:opacity-80'
            style={{ width: 22, height: 22, lineHeight: 0 }}
            aria-label='Close'>
            <svg width='22' height='22' viewBox='0 0 24 24' aria-hidden='true'>
              <path d='M6.4 6.4 L17.6 17.6 M17.6 6.4 L6.4 17.6' fill='none' stroke='rgba(255,255,255,0.8)' strokeWidth='2' strokeLinecap='round' />
            </svg>
          </button>

          <div className='flex min-h-0 flex-1 flex-col items-center justify-center gap-3'>
            <img src='/playcast-gibber.svg' alt='Playcast' className='w-[139px] h-[112px] shrink-0 object-contain' />
            <h1 className='shrink-0 text-[32px] font-bold tracking-tight text-[#FBBF24] font-["Inter"]'>Playcast</h1>
            {gateTimedOut ? (
              <>
                <p className='text-center text-sm text-[#FCA5A5]'>
                  Taking too long to connect. The background service may not be running.
                </p>
                <button type='button' onClick={handleRetryGates}
                  className='rounded-full border border-[#FFFFFF]/15 bg-[#FFFFFF]/8 px-6 py-2 text-sm font-medium text-white transition-opacity hover:opacity-80'>
                  Retry
                </button>
                <button type='button' onClick={handleSignOut}
                  className='text-xs text-[#FFFFFF]/50 transition-opacity hover:opacity-80'>
                  Sign out
                </button>
              </>
            ) : (
              <div className='mt-5 shrink-0 rounded-full bg-[#FBBF24]/60 px-12 py-2.5 flex items-center justify-center cursor-default'>
                <div className='h-6 w-6 animate-spin rounded-full border-[3px] border-[#020617]/20 border-t-[#020617]' />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <RewardsCoordinatorContext.Provider value={coordinator}>
      <div className='box-border flex h-full min-h-0 w-full flex-1 overflow-hidden' style={{ background: '#3D2A69' }}>
        <div
          className={`relative box-border flex h-full min-h-0 w-full flex-col overflow-hidden ${PAVILION_SHELL_RADIUS} ${PAVILION_SHELL_BORDER} ${PAVILION_SHELL_GRADIENT} pt-[14px] pb-[14px] px-3`}
          style={{ color: '#FFFFFF' }}
        >
          <ShellToastProvider>
            {!schedulePageVisible && (
              <button type='button' onClick={handleClose}
                className='absolute right-[14px] top-[14px] z-50 flex items-center justify-center transition-opacity hover:opacity-80'
                style={{ width: 22, height: 22, lineHeight: 0 }}
                aria-label='Close'>
                <svg width='22' height='22' viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M6.4 6.4 L17.6 17.6 M17.6 6.4 L6.4 17.6' fill='none' stroke='rgba(255,255,255,0.8)' strokeWidth='2' strokeLinecap='round' />
                </svg>
              </button>
            )}
            {renderScreen()}
          </ShellToastProvider>

          {/* Sign-out confirmation dialog */}
          {showSignOutConfirm && (
            <>
              <div className='absolute inset-0 z-[60] rounded-[12px] bg-[#0b0618] opacity-[0.72]' />
              <div className='absolute inset-0 z-[70] flex items-center justify-center p-4'>
                <div className='w-full rounded-[10px] border border-[#FFFFFF]/15 bg-[#0b0618] p-4'>
                  <p className='mb-2 text-center text-base font-semibold text-white'>Sign out?</p>
                  <p className='mb-4 text-center text-xs text-[#FFFFFF]/80'>
                    You'll need to sign back in to earn and redeem rewards on this machine.
                  </p>
                  <div className='flex flex-col gap-2'>
                    <button type='button' onClick={async () => {
                      try {
                        await handleSignOut();
                      } catch {
                        // If sign-out fails, dismiss dialog so user can retry
                      } finally {
                        setShowSignOutConfirm(false);
                      }
                    }}
                      className='flex w-full min-h-[44px] items-center justify-center rounded-full bg-[#FCA5A5] text-[15px] font-semibold text-[#020617] transition-transform hover:scale-[1.02] active:scale-[0.98]'>
                      Sign out
                    </button>
                    <button type='button' onClick={() => setShowSignOutConfirm(false)}
                      className='flex w-full min-h-[44px] items-center justify-center rounded-full text-sm font-medium text-[#FFFFFF]/80 transition-opacity hover:opacity-80'>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </RewardsCoordinatorContext.Provider>
  );
};
